import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { hasPermission } from '../services/authService';
import { useAdminSocket } from '../context/AdminSocketContext';
import { renderWithLinks } from '../utils/linkify';
import './AdminChat.css';

const AdminChat = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [savedSessions, setSavedSessions] = useState([]);
    const [tab, setTab] = useState('active'); // 'active' | 'saved'
    const [scripts, setScripts] = useState([]);
    const [showScriptsModal, setShowScriptsModal] = useState(false);
    const [scriptForm, setScriptForm] = useState({ id: null, name: '', content: '' });
    const [showScriptDropdown, setShowScriptDropdown] = useState(false);
    const [filteredScripts, setFilteredScripts] = useState([]);
    const [selectedScriptIndex, setSelectedScriptIndex] = useState(0);

    const [activeSession, setActiveSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const { socket, isOfflineMode, toggleOfflineMode, onlineClients, unreadSessions, markSessionAsRead } = useAdminSocket();
    const [adminProfile, setAdminProfile] = useState(null);
    const messagesEndRef = useRef(null);
    const activeSessionRef = useRef(activeSession);
    const inputRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState('');
    const searchTimeoutRef = useRef(null);

    const [replyTo, setReplyTo] = useState(null);

    useEffect(() => {
        if (replyTo && inputRef.current) {
            inputRef.current.focus();
        }
    }, [replyTo]);

    const getSenderName = (m) => {
        if (m.senderType === 'operator') {
            return m.adminName || 'Support';
        }
        return activeSession?.user 
            ? `${activeSession.user.name} ${activeSession.user.surname}` 
            : (activeSession?.guestName || 'Guest');
    };

    const handlePointerDown = (e, msg) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        
        const bubble = e.currentTarget;
        const startX = e.clientX;
        let diffX = 0;

        let indicator = bubble.querySelector('.swipe-reply-indicator');
        if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'swipe-reply-indicator';
            indicator.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="color: #94a3b8;"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>`;
            indicator.style.position = 'absolute';
            indicator.style.left = '-30px';
            indicator.style.top = '50%';
            indicator.style.transform = 'translateY(-50%) scale(0.5)';
            indicator.style.opacity = '0';
            indicator.style.transition = 'transform 0.1s, opacity 0.1s';
            bubble.appendChild(indicator);
        }

        const onPointerMove = (moveEvent) => {
            diffX = moveEvent.clientX - startX;
            if (diffX > 8) {
                bubble.style.transform = `translateX(${Math.min(diffX, 60)}px)`;
                bubble.style.cursor = 'grabbing';
                
                const progress = Math.min(diffX / 50, 1);
                indicator.style.transform = `translateY(-50%) scale(${0.5 + progress * 0.5})`;
                indicator.style.opacity = progress.toString();
            }
        };

        const onPointerUp = () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);

            bubble.style.transition = 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)';
            bubble.style.transform = '';
            bubble.style.cursor = '';
            
            indicator.style.transform = 'translateY(-50%) scale(0.5)';
            indicator.style.opacity = '0';
            
            setTimeout(() => {
                bubble.style.transition = '';
            }, 200);

            if (diffX > 50) {
                setReplyTo({
                    id: msg.id,
                    content: msg.content,
                    sender: getSenderName(msg)
                });
            }
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    };

    const [loadingSaved, setLoadingSaved] = useState(false);
    const [savedLoaded, setSavedLoaded] = useState(false);

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchSavedSessions(query);
        }, 300);
    };

    const fetchSavedSessions = async (searchVal = '') => {
        setLoadingSaved(true);
        try {
            const url = searchVal 
                ? `${API_BASE_URL}/chats/sessions/saved?search=${encodeURIComponent(searchVal)}`
                : `${API_BASE_URL}/chats/sessions/saved`;
            const res = await axios.get(url, { withCredentials: true });
            setSavedSessions(res.data);
            setSavedLoaded(true);
        } catch (error) {
            console.error('Failed to fetch saved sessions', error);
        } finally {
            setLoadingSaved(false);
        }
    };

    const [messageSearchQuery, setMessageSearchQuery] = useState('');

    useEffect(() => {
        setMessageSearchQuery('');
    }, [activeSession?.id]);

    // Lazy load saved sessions when switching to 'saved' tab
    useEffect(() => {
        if (tab === 'saved' && !savedLoaded && !loadingSaved) {
            fetchSavedSessions();
        }
    }, [tab, savedLoaded, loadingSaved]);

    // Guard: only users with admin.chat permission can access this page
    useEffect(() => {
        if (!hasPermission('admin.chat')) {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        activeSessionRef.current = activeSession;
    }, [activeSession]);

    // Auto-scroll inside chat and focus input
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [messages, activeSession]);

    useEffect(() => {
        // Fetch initial active data and scripts only (saved sessions lazy-loaded on tab switch)
        const fetchData = async () => {
            try {
                const [resActive, resScripts] = await Promise.all([
                    axios.get(`${API_BASE_URL}/chats/sessions`, { withCredentials: true }),
                    axios.get(`${API_BASE_URL}/chat-scripts`, { withCredentials: true })
                ]);
                setSessions(resActive.data);
                setScripts(resScripts.data);
            } catch (error) {
                console.error('Failed to fetch chat data', error);
            }
        };
        fetchData();

        const fetchAdminProfile = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/users/me`, { withCredentials: true });
                setAdminProfile(res.data);
            } catch (err) {
                console.error('Failed to fetch admin profile', err);
            }
        };
        fetchAdminProfile();

        if (!socket) return;

        // Reset socket listeners for this specific component
        // Note: The global AdminSocketContext already handles 'join_admin' and notification sounds

        const handleNewSession = (session) => {
            setSessions((prev) => {
                const exists = prev.find(s => s.id === session.id);
                if (exists) {
                    return prev.map(s => s.id === session.id ? session : s);
                }
                return [session, ...prev];
            });
            // New incoming session logic handled by AdminSocketContext for unread status
        };

        const handleChatUpdated = ({ sessionId, message }) => {
            // Note: Sound is played globally by AdminSocketContext

            // If this is the active session, append message if not already present
            if (activeSessionRef.current && activeSessionRef.current.id === sessionId) {
                setMessages((prev) => {
                    const exists = prev.find(m => m.id === message.id);
                    if (exists) return prev;
                    return [...prev, message];
                });
                // Mark as seen immediately if it's the active window
                localStorage.setItem(`chat_seen_${sessionId}`, String(message.id));
                markSessionAsRead(sessionId); // Clear global unread state for active chat
            }

            // Update session metadata and bubble to top
            setSessions(prev => {
                const s = prev.find(x => x.id === sessionId);
                if (s) {
                    const updated = {
                        ...s,
                        lastMessage: message,
                        isResponded: s.isResponded || message.senderType === 'operator'
                    };
                    return [updated, ...prev.filter(x => x.id !== sessionId)];
                }
                return prev;
            });
            // Update in saved sessions if present
            setSavedSessions(prev => {
                const s = prev.find(x => x.id === sessionId);
                if (s) {
                    const updated = {
                        ...s,
                        lastMessage: message,
                        isResponded: s.isResponded || message.senderType === 'operator'
                    };
                    return [updated, ...prev.filter(x => x.id !== sessionId)];
                }
                return prev;
            });
        };

        socket.on('new_session', handleNewSession);
        socket.on('chat_updated', handleChatUpdated);

        return () => {
            socket.off('new_session', handleNewSession);
            socket.off('chat_updated', handleChatUpdated);
        };
    }, [socket, markSessionAsRead]);

    const selectSession = async (session) => {
        setActiveSession(session);
        // Clear unread flag when admin opens the session
        markSessionAsRead(session.id);
        // Persist "seen" status in localStorage to survive refresh
        if (session.lastMessage) {
            localStorage.setItem(`chat_seen_${session.id}`, String(session.lastMessage.id));
        }
        if (socket) {
            socket.emit('join_session', { sessionId: session.id });
        }
        try {
            const res = await axios.get(`${API_BASE_URL}/chats/session/${session.id}`, { withCredentials: true });
            setMessages(res.data.messages || []);
        } catch (err) {
            console.error("Failed to load session messages", err);
        }
    };

    const closeSession = async (session) => {
        if (!window.confirm("Are you sure you want to close this chat?")) return;
        try {
            await axios.patch(`${API_BASE_URL}/chats/session/${session.id}/close`, {}, { withCredentials: true });
            setSessions(prev => prev.filter(s => s.id !== session.id));
            if (activeSession && activeSession.id === session.id) {
                setActiveSession(null);
                setMessages([]);
            }
        } catch (err) {
            console.error("Failed to close session", err);
        }
    };

    const handleSaveScript = async (e) => {
        e.preventDefault();
        try {
            const payload = { name: scriptForm.name, content: scriptForm.content };
            if (scriptForm.id) {
                const res = await axios.put(`${API_BASE_URL}/chat-scripts/${scriptForm.id}`, payload, { withCredentials: true });
                setScripts(prev => prev.map(s => s.id === scriptForm.id ? res.data : s));
            } else {
                const res = await axios.post(`${API_BASE_URL}/chat-scripts`, payload, { withCredentials: true });
                setScripts(prev => [...prev, res.data].sort((a,b) => a.name.localeCompare(b.name)));
            }
            setScriptForm({ id: null, name: '', content: '' });
        } catch (err) {
            console.error("Failed to save script", err);
            alert(err.response?.data?.error || "Failed to save script");
        }
    };

    const handleDeleteScript = async (id) => {
        if (!window.confirm("Are you sure you want to delete this script?")) return;
        try {
            await axios.delete(`${API_BASE_URL}/chat-scripts/${id}`, { withCredentials: true });
            setScripts(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            console.error("Failed to delete script", err);
            alert("Failed to delete script");
        }
    };

    const toggleSaveSession = async (session) => {
        try {
            const res = await axios.patch(`${API_BASE_URL}/chats/session/${session.id}/save`, {}, { withCredentials: true });
            const updatedSession = {...session, isSaved: res.data.session.isSaved};
            
            if (updatedSession.isSaved) {
                setSavedSessions(prev => [updatedSession, ...prev.filter(s => s.id !== session.id)]);
            } else {
                setSavedSessions(prev => prev.filter(s => s.id !== session.id));
            }
            
            // Update in sessions list too
            setSessions(prev => prev.map(s => s.id === session.id ? updatedSession : s));
            
            if (activeSession && activeSession.id === session.id) {
                setActiveSession(updatedSession);
            }
        } catch (err) {
            console.error("Failed to toggle save session", err);
            alert("Failed to save session");
        }
    };

    const handleInputClick = () => {
        // Just in case we need to trigger dropdown on click when cursor is near a command
        checkSlashCommand(newMessage, inputRef.current?.selectionStart || newMessage.length);
    };

    const checkSlashCommand = (text, cursorPos) => {
        if (!text) {
            setShowScriptDropdown(false);
            return;
        }
        
        // Look backwards from cursor for a slash command
        const textBeforeCursor = text.substring(0, cursorPos);
        const match = textBeforeCursor.match(/(^|\s)\/([a-z0-9_-]*)$/i);
        
        if (match) {
            const query = match[2].toLowerCase();
            const filtered = scripts.filter(s => s.name.toLowerCase().includes(query));
            if (filtered.length > 0) {
                setFilteredScripts(filtered);
                setSelectedScriptIndex(0);
                setShowScriptDropdown(true);
            } else {
                setShowScriptDropdown(false);
            }
        } else {
            setShowScriptDropdown(false);
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        const cursorPos = e.target.selectionStart;
        setNewMessage(val);
        checkSlashCommand(val, cursorPos);
    };

    const insertScript = (script) => {
        if (!inputRef.current) return;
        
        const cursorPos = inputRef.current.selectionStart;
        const textBeforeCursor = newMessage.substring(0, cursorPos);
        const textAfterCursor = newMessage.substring(cursorPos);
        
        // Find the slash that originated this match
        const match = textBeforeCursor.match(/(^|\s)\/([a-z0-9_-]*)$/i);
        if (match) {
            const slashPos = textBeforeCursor.lastIndexOf(match[0]) + match[1].length;
            const newText = newMessage.substring(0, slashPos) + script.content + textAfterCursor;
            setNewMessage(newText);
            setShowScriptDropdown(false);
            
            // Focus and set cursor after the inserted text
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    const newCursorPos = slashPos + script.content.length;
                    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        }
    };

    const handleInputKeyDown = (e) => {
        if (showScriptDropdown) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedScriptIndex((prev) => (prev + 1) % filteredScripts.length);
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedScriptIndex((prev) => (prev - 1 + filteredScripts.length) % filteredScripts.length);
                return;
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertScript(filteredScripts[selectedScriptIndex]);
                return;
            } else if (e.key === 'Escape') {
                setShowScriptDropdown(false);
            }
        }

        if (e.key === 'Enter' && !e.shiftKey && !showScriptDropdown) {
            e.preventDefault();
            sendMessage(e);
        }
    };

    const sendMessage = (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !socket || !activeSession) return;

        let adminName = 'Support';
        if (adminProfile) {
            adminName = (adminProfile.name || adminProfile.username || 'System').trim();
        }

        const messagePayload = {
            sessionId: activeSession.id,
            senderType: 'operator',
            content: newMessage,
            adminName
        };

        if (replyTo) {
            messagePayload.replyToId = replyTo.id;
            messagePayload.replyToContent = replyTo.content;
            messagePayload.replyToSender = replyTo.sender;
        }

        socket.emit('send_message', messagePayload, (res) => {
            if (!res.success) {
                console.error("Failed to send message", res.error);
                alert("Failed to send message: " + res.error);
            }
        });

        setNewMessage('');
        setReplyTo(null);
    };

    const isSessionOnline = (s) => {
        return onlineClients.has(s.guestId || String(s.userId));
    };

    const activeChats = sessions.filter(isSessionOnline);

    const offlineActiveChats = sessions.filter(s => !isSessionOnline(s));
    const uniqueSavedSessions = savedSessions.filter(
        saved => !offlineActiveChats.some(off => off.id === saved.id)
    );

    const matchesSearch = (s) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const name = (s.user ? `${s.user.name} ${s.user.surname}` : (s.guestName ? s.guestName : `Guest ${s.guestId}`)).toLowerCase();
        const lastMsg = (s.lastMessage ? s.lastMessage.content : '').toLowerCase();
        const guestId = (s.guestId || '').toLowerCase();
        return name.includes(query) || lastMsg.includes(query) || guestId.includes(query);
    };

    const historyChats = [
        ...offlineActiveChats.filter(matchesSearch),
        ...uniqueSavedSessions
    ].sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

    return (
        <div className="admin-chat-container">
            <div className="admin-chat-sidebar">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px 0 20px', backgroundColor: '#ffffff' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Chats</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button 
                            onClick={() => setShowScriptsModal(true)}
                            title="Manage Chat Scripts"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                        </button>
                        <div className="offline-toggle-container">
                            <span style={{ fontSize: '13px', fontWeight: 600, color: isOfflineMode ? '#ef4444' : '#10b981' }}>
                                {isOfflineMode ? 'Offline' : 'Online'}
                            </span>
                            <div
                                onClick={toggleOfflineMode}
                                style={{
                                    position: 'relative',
                                    width: '36px',
                                    height: '20px',
                                    background: isOfflineMode ? '#ef4444' : '#10b981',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    background: 'white',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    left: isOfflineMode ? '18px' : '2px',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                }}></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', padding: '10px 20px', gap: '10px', backgroundColor: '#ffffff' }}>
                    <button 
                        onClick={() => setTab('active')} 
                        style={{ flex: 1, padding: '6px 0', border: 'none', background: tab === 'active' ? '#eff6ff' : 'transparent', color: tab === 'active' ? '#3b82f6' : '#64748b', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}
                    >
                        Active ({activeChats.length})
                    </button>
                    <button 
                        onClick={() => setTab('saved')} 
                        style={{ flex: 1, padding: '6px 0', border: 'none', background: tab === 'saved' ? '#eff6ff' : 'transparent', color: tab === 'saved' ? '#3b82f6' : '#64748b', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}
                    >
                        Saved / History
                    </button>
                </div>
                {tab === 'saved' && (
                    <div style={{ padding: '0 20px 10px 20px', backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                            type="text"
                            placeholder="Search history..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                )}
                {tab === 'saved' && loadingSaved ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>Loading saved history...</div>
                ) : (tab === 'active' ? activeChats : historyChats).length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No {tab === 'active' ? 'active' : 'history'} chats.</div>
                ) : null}
                <ul className="chat-session-list">
                    {(tab === 'active' ? activeChats : historyChats).map(s => {
                        const name = s.user ? `${s.user.name} ${s.user.surname}` : (s.guestName ? s.guestName : `Guest ${s.guestId?.substring(0, 6)}...`);
                        const initial = name ? name.charAt(0).toUpperCase() : 'G';
                        const isUnread = unreadSessions.has(s.id);
                        return (
                            <li
                                key={s.id}
                                className={[
                                    activeSession?.id === s.id ? 'active' : '',
                                    isUnread ? 'unread' : ''
                                ].filter(Boolean).join(' ')}
                                onClick={() => selectSession(s)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, overflow: 'hidden' }}>
                                    <div style={{ position: 'relative' }}>
                                        <div className="session-avatar">{initial}</div>
                                        {isUnread && <span className="unread-dot" />}
                                    </div>
                                    <div className="session-info">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span className="session-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {name}
                                                <span
                                                    title={onlineClients.has(s.guestId || String(s.userId)) ? "Online" : "Offline"}
                                                    style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: onlineClients.has(s.guestId || String(s.userId)) ? '#10b981' : '#94a3b8' }}
                                                />
                                            </span>
                                            {!s.isResponded && (
                                                <span className="not-opened-icon" title="No agent has replied yet">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#f59e0b' }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px', gap: '8px' }}>
                                            <span className="session-last-msg" style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                {s.lastMessage ? s.lastMessage.content : 'No messages yet'}
                                            </span>
                                            <span className="session-time" style={{ flexShrink: 0, fontSize: '11px', color: '#94a3b8' }}>
                                                {tab === 'saved' ? (
                                                    `${new Date(s.createdAt).toLocaleDateString()} ${new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                ) : (
                                                    new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="close-btn"
                                    onClick={(e) => { e.stopPropagation(); closeSession(s); }}
                                    title="Close Chat"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="admin-chat-main">
                {activeSession ? (
                    <>
                        <div className="chat-main-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {activeSession.user ? `${activeSession.user.name} ${activeSession.user.surname}` : (activeSession.guestName ? activeSession.guestName : `Guest ${activeSession.guestId?.substring(0, 6)}`)}
                                    <span
                                        style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', color: 'white', backgroundColor: onlineClients.has(activeSession.guestId || String(activeSession.userId)) ? '#10b981' : '#94a3b8' }}
                                    >
                                        {onlineClients.has(activeSession.guestId || String(activeSession.userId)) ? "Online" : "Offline"}
                                    </span>
                                </h4>
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                    Started on {new Date(activeSession.createdAt).toLocaleDateString()} at {new Date(activeSession.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        placeholder="Find in chat..."
                                        value={messageSearchQuery}
                                        onChange={(e) => setMessageSearchQuery(e.target.value)}
                                        style={{
                                            padding: '6px 10px 6px 28px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '12px',
                                            outline: 'none',
                                            width: '140px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ position: 'absolute', left: '8px', color: '#94a3b8' }}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                    {messageSearchQuery && (
                                        <button 
                                            type="button"
                                            onClick={() => setMessageSearchQuery('')}
                                            style={{ position: 'absolute', right: '8px', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: '10px' }}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                <button 
                                    onClick={() => toggleSaveSession(activeSession)}
                                    title={activeSession.isSaved ? "Unsave Chat" : "Save Chat"}
                                    style={{ 
                                        background: activeSession.isSaved ? '#eff6ff' : 'transparent', 
                                        border: '1px solid', 
                                        borderColor: activeSession.isSaved ? '#bfdbfe' : '#e2e8f0', 
                                        borderRadius: '6px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        padding: '8px', 
                                        cursor: 'pointer',
                                        color: activeSession.isSaved ? '#3b82f6' : '#64748b',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill={activeSession.isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
                                </button>
                            </div>
                        </div>
                        <div className="chat-main-messages">
                            {messageSearchQuery && (
                                <div style={{ background: '#f1f5f9', color: '#475569', padding: '8px 16px', borderRadius: '6px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span>Found {messages.filter(m => m.content.toLowerCase().includes(messageSearchQuery.toLowerCase())).length} results for "<strong>{messageSearchQuery}</strong>"</span>
                                    <button 
                                        onClick={() => setMessageSearchQuery('')}
                                        style={{ border: 'none', background: 'none', color: '#3b82f6', fontWeight: 600, cursor: 'pointer', fontSize: '12px' }}
                                    >
                                        Show All
                                    </button>
                                </div>
                            )}
                            {messages.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8', marginTop: '32px' }}>History is empty. Send a message to start.</p> : null}
                            {messages.filter(m => !messageSearchQuery || m.content.toLowerCase().includes(messageSearchQuery.toLowerCase())).map((m, idx) => (
                                <div 
                                    key={m.id || idx} 
                                    id={m.id ? `msg-${m.id}` : undefined}
                                    className={`admin-chat-bubble admin-${m.senderType}`}
                                    onPointerDown={(e) => handlePointerDown(e, m)}
                                    style={{ cursor: 'grab', touchAction: 'pan-y' }}
                                >
                                    <button
                                        type="button"
                                        className="bubble-reply-btn"
                                        title="Reply"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setReplyTo({
                                                id: m.id,
                                                content: m.content,
                                                sender: getSenderName(m)
                                            });
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>
                                    </button>
                                    {m.replyToContent && (
                                        <div className="chat-reply-quoted" style={{
                                            background: 'rgba(0, 0, 0, 0.08)',
                                            borderLeft: '4px solid #fff',
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            marginBottom: '6px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            color: '#e2e8f0'
                                        }} onClick={(e) => {
                                            e.stopPropagation();
                                            const el = document.getElementById(`msg-${m.replyToId}`);
                                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '10.5px', opacity: 0.9 }}>
                                                {m.replyToSender}
                                            </div>
                                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.8 }}>
                                                {m.replyToContent}
                                            </div>
                                        </div>
                                    )}
                                    {m.senderType === 'operator' && m.adminName && (
                                        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', opacity: 0.8, color: '#e2e8f0' }}>
                                            {m.adminName}
                                        </div>
                                    )}
                                    {renderWithLinks(m.content)}
                                    <div className="chat-timestamp" style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7, textAlign: m.senderType === 'operator' ? 'right' : 'left' }}>
                                        {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            {replyTo && (
                                <div style={{
                                    background: '#f1f5f9',
                                    borderLeft: '4px solid #3b82f6',
                                    padding: '8px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    margin: '0 24px',
                                    borderRadius: '8px 8px 0 0',
                                    border: '1px solid #e2e8f0',
                                    borderBottom: 'none'
                                }}>
                                    <div style={{ overflow: 'hidden', flex: 1 }}>
                                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#3b82f6' }}>Replying to {replyTo.sender}</div>
                                        <div style={{ fontSize: '12px', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyTo.content}</div>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => setReplyTo(null)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', marginLeft: '12px' }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            {showScriptDropdown && filteredScripts.length > 0 && (
                                <div className="script-dropdown">
                                    <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>
                                        Insert Script
                                    </div>
                                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredScripts.map((sc, idx) => (
                                            <li 
                                                key={sc.id} 
                                                className={`script-dropdown-item ${idx === selectedScriptIndex ? 'selected' : ''}`}
                                                onClick={() => insertScript(sc)}
                                                onMouseEnter={() => setSelectedScriptIndex(idx)}
                                            >
                                                <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>/{sc.name}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sc.content}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <form className="chat-main-input" onSubmit={sendMessage}>
                                <textarea
                                    ref={inputRef}
                                    placeholder="Type your reply... (Press Enter to send, Shift+Enter for new line, / for scripts)"
                                    value={newMessage}
                                    onChange={handleInputChange}
                                    onKeyDown={handleInputKeyDown}
                                    onClick={handleInputClick}
                                    onKeyUp={handleInputClick}
                                    rows="1"
                                />
                                <button type="submit" disabled={!newMessage.trim()}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '-2px', marginTop: '2px' }}><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="no-chat-selected">
                        <svg className="no-chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                        <p style={{ margin: 0 }}>Select a chat from the sidebar to view or reply.</p>
                    </div>
                )}
            </div>

            {/* Chat Scripts Modal */}
            {showScriptsModal && (
                <div className="script-modal-overlay" onClick={() => setShowScriptsModal(false)}>
                    <div className="script-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="script-modal-header">
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Manage Chat Scripts</h3>
                            <button className="close-btn" onClick={() => setShowScriptsModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>
                        <div className="script-modal-body">
                            <form className="script-form" onSubmit={handleSaveScript}>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Command Name (e.g. /greeting)</label>
                                        <div style={{ position: 'relative' }}>
                                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: 600 }}>/</span>
                                            <input 
                                                type="text" 
                                                value={scriptForm.name} 
                                                onChange={e => setScriptForm({...scriptForm, name: e.target.value})} 
                                                required
                                                placeholder="greeting"
                                                style={{ width: '100%', padding: '10px 12px 10px 24px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ alignSelf: 'flex-end' }}>
                                        <button type="submit" style={{ padding: '10px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                                            {scriptForm.id ? "Update" : "Add Script"}
                                        </button>
                                        {scriptForm.id && (
                                            <button type="button" onClick={() => setScriptForm({ id: null, name: '', content: '' })} style={{ marginLeft: '8px', padding: '10px 16px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div style={{ marginTop: '12px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Script Content</label>
                                    <textarea 
                                        value={scriptForm.content} 
                                        onChange={e => setScriptForm({...scriptForm, content: e.target.value})} 
                                        required
                                        placeholder="Hello! How can I help you today?"
                                        rows="3"
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'vertical' }}
                                    />
                                </div>
                            </form>
                            <div className="scripts-list-container" style={{ marginTop: '24px' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#64748b' }}>Existing Scripts</h4>
                                {scripts.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>No scripts saved yet.</p> : null}
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {scripts.map(sc => (
                                        <li key={sc.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>/{sc.name}</div>
                                                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{sc.content}</div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                <button onClick={() => setScriptForm(sc)} style={{ padding: '6px', background: '#eff6ff', color: '#3b82f6', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
                                                <button onClick={() => handleDeleteScript(sc.id)} style={{ padding: '6px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminChat;
